import { VENDOR_DOCUMENT_TYPES } from '../constants';

export const MAX_VENDOR_DOC_BYTES = 5 * 1024 * 1024;
export const ALLOWED_VENDOR_DOC_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
];

export const PHARMA_VENDOR_TYPES = new Set([
  'PHARMACEUTICAL_SUPPLIER',
  'PHARMACY_VENDOR',
]);

const VENDOR_SUBMIT_FIELDS = [
  { key: 'vendor_code', label: 'Vendor code', aliases: ['vendor_code'] },
  { key: 'vendor_name', label: 'Vendor name', aliases: ['vendor_name'] },
  { key: 'vendor_type', label: 'Vendor type', aliases: ['vendor_type'] },
  { key: 'address', label: 'Address', aliases: ['address', 'address_line1'] },
  { key: 'state', label: 'State', aliases: ['state'] },
  { key: 'city', label: 'City', aliases: ['city'] },
  { key: 'country', label: 'Country', aliases: ['country'] },
  { key: 'pincode', label: 'PIN / ZIP', aliases: ['pincode', 'postal_code'] },
  { key: 'contact_person', label: 'Contact person', aliases: ['contact_person'] },
  { key: 'phone', label: 'Mobile number', aliases: ['phone', 'contact_phone'] },
  { key: 'email', label: 'Email', aliases: ['email', 'contact_email'] },
  { key: 'gstin', label: 'GST number', aliases: ['gstin', 'gst_number'] },
  { key: 'pan', label: 'PAN number', aliases: ['pan', 'pan_number'] },
  { key: 'payment_terms', label: 'Payment terms', aliases: ['payment_terms'] },
  { key: 'credit_period', label: 'Credit period', aliases: ['credit_period', 'credit_period_days'] },
  { key: 'currency', label: 'Currency', aliases: ['currency'] },
];

function vendorFieldValue(vendor, aliases) {
  for (let i = 0; i < aliases.length; i += 1) {
    const raw = vendor?.[aliases[i]];
    if (raw == null) continue;
    const text = String(raw).trim();
    if (text) return text;
  }
  return '';
}

/** Mandatory VendorForm fields still empty on a DRAFT (blocks Submit for Approval only). */
export function vendorSubmitMissingFields(vendor) {
  if (!vendor) return VENDOR_SUBMIT_FIELDS.map((f) => f.label);
  const missing = [];
  VENDOR_SUBMIT_FIELDS.forEach((field) => {
    const value = vendorFieldValue(vendor, field.aliases);
    if (field.key === 'phone') {
      if (!value) missing.push(field.label);
      else if (!/^[6-9]\d{9}$/.test(value)) missing.push('Valid 10-digit mobile number');
      return;
    }
    if (field.key === 'email') {
      if (!value) missing.push(field.label);
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) missing.push('Valid email');
      return;
    }
    if (!value) missing.push(field.label);
  });
  const type = vendorFieldValue(vendor, ['vendor_type']);
  if (PHARMA_VENDOR_TYPES.has(type)) {
    if (!vendorFieldValue(vendor, ['drug_license_no', 'drug_license_number'])) {
      missing.push('Drug License No.');
    }
    if (!vendorFieldValue(vendor, ['license_valid_till', 'drug_license_valid_until'])) {
      missing.push('License valid till');
    }
  }
  return missing;
}

export function vendorSubmitBlockReason(vendor) {
  const missing = vendorSubmitMissingFields(vendor);
  if (!missing.length) return '';
  const shown = missing.slice(0, 6);
  const extra = missing.length - shown.length;
  const list = extra > 0 ? `${shown.join(', ')} (+${extra} more)` : shown.join(', ');
  return `Fill required vendor fields before submitting for approval: ${list}.`;
}

/** Document types available in the add dropdown (PAN is captured in the main form). */
export const VENDOR_UPLOAD_DOC_TYPES = VENDOR_DOCUMENT_TYPES.filter(d => d.key !== 'PAN_CARD');

export function emptyDocumentRow(type = '') {
  return {
    type,
    document_number: '',
    file_name: '',
    mime_type: '',
    content_base64: '',
    _fromApi: false,
  };
}

export function emptyVendorForm() {
  return {
    vendor_code: '',
    vendor_name: '',
    legal_name: '',
    vendor_type: '',
    is_active: true,
    vendor_category: '',
    remarks: '',
    address: '',
    address_line2: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    contact_person: '',
    phone: '',
    alternate_phone: '',
    email: '',
    gstin: '',
    pan: '',
    drug_license_no: '',
    license_valid_till: '',
    payment_terms: '',
    credit_period: '',
    rating: '',
    bank_name: '',
    bank_account: '',
    bank_ifsc: '',
    currency: 'INR',
    documentRows: [],
  };
}

/** API documents object → form rows. */
export function documentsToRows(documents) {
  if (!documents || typeof documents !== 'object') return [];
  return Object.entries(documents).map(([type, val]) => ({
    type,
    document_number: val?.document_number ?? '',
    file_name: val?.file_name ?? '',
    mime_type: val?.mime_type ?? '',
    content_base64: '',
    _fromApi: true,
  }));
}

/** Form rows → API documents object `{}`. */
export function rowsToDocuments(rows, existingDocuments = {}) {
  const out = {};
  const existing = existingDocuments && typeof existingDocuments === 'object' ? existingDocuments : {};

  (rows || []).forEach((row) => {
    if (!row?.type) return;
    const entry = {};
    const num = (row.document_number ?? '').trim();
    if (num) entry.document_number = num;
    if (row.file_name) entry.file_name = row.file_name;
    if (row.mime_type) entry.mime_type = row.mime_type;

    if (row.content_base64) {
      entry.content_base64 = row.content_base64;
    } else if (row._fromApi && existing[row.type]) {
      const prev = existing[row.type];
      if (prev.content_base64) entry.content_base64 = prev.content_base64;
      if (!entry.file_name && prev.file_name) entry.file_name = prev.file_name;
      if (!entry.mime_type && prev.mime_type) entry.mime_type = prev.mime_type;
      if (!entry.document_number && prev.document_number) entry.document_number = prev.document_number;
    }

    if (Object.keys(entry).length > 0) out[row.type] = entry;
  });

  return out;
}

export function isPendingApproval(status) {
  return status === 'PENDING_APPROVAL' || status === 'SUBMITTED';
}

export function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read file'));
        return;
      }
      resolve(result.split(',')[1] || result);
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export function validateVendorFile(file) {
  if (!file) return 'No file selected';
  if (!ALLOWED_VENDOR_DOC_MIME.includes(file.type)) return 'Allowed formats: PDF, JPG, PNG';
  if (file.size > MAX_VENDOR_DOC_BYTES) return 'File must be 5 MB or smaller';
  return null;
}

export function documentTypeLabel(key) {
  return VENDOR_DOCUMENT_TYPES.find(d => d.key === key)?.label
    ?? key.replace(/_/g, ' ');
}

export function documentTypeMeta(key) {
  return VENDOR_DOCUMENT_TYPES.find(d => d.key === key);
}

/** Stable id for vendor list rows (API may use vendor_id or id). */
export function vendorRowId(vendor) {
  return vendor?.vendor_id ?? vendor?.id ?? '';
}

/** Display label for a vendor record. */
export function formatVendorLabel(vendor) {
  if (!vendor) return '';
  const name = vendor.vendor_name || vendor.name || '';
  if (!name) return vendorRowId(vendor) || '—';
  return vendor.vendor_code ? `${name} (${vendor.vendor_code})` : name;
}

/** Resolve vendor id → display name from a loaded vendor list. */
export function resolveVendorNameFromList(vendorId, vendors = []) {
  if (!vendorId) return '—';
  const v = vendors.find(x => vendorRowId(x) === vendorId);
  return v ? formatVendorLabel(v) : vendorId;
}
